import { AnyAction } from "redux";
import { FolderProps } from "../../components/Files/documents/types";
import { Citation } from "../actions/ChatAction";
import { DropdownOption } from "../../components/shared/TailwindComponents/Dropdown";
import { PlaybookListItem } from "../../types/playbook";
import { TableTemplate } from "../../types/batchTables";
import type { DocumentInfo } from "../../components/Files/documents/DocumentInfoModal";

export interface FolderOption {
  id: string;
  label: string;
}

export interface ImportSummary {
  added: { name: string; email: string }[];
  updated: { name: string; email: string }[];
  errors: { client: string; error: string }[];
  total_added: number;
  total_updated: number;
  total_errors: number;
}

/**
 * Every modal `type` GlobalModal can render. Extracted from ModalState so the
 * canonical GlobalModalPayload (below) can reference it.
 */
export type ModalType = 'confirmation' | 'feedback' | 'custom' | 'success' | 'error' | 'warning' | 'share' | 'sign' | 'create' | 'create_folder' | 'addFile' | 'forgotPassword' | 'password' | 'payment-options' | "tips" | 'info' | 'receiver-options' | "addSignature" | 'promptfeedback' | "2fa-qrcode" | 'alert' | 'otp-verification' | 'addTask' | 'confirm-upload' | 'subscription-modal' | 'timeStop' | 'redirect' | 'anonymize' | 'translate' | 'license-request' | 'resetPassword' | 'citations' | 'citation' | 'vaultFilePicker' | "suggestedTasks" | 'sign_doc' | 'attachExistingCaseToClient' | 'ai-settings' | 'settings' | 'addTaskComment' | 'workflows' | 'importClients' | 'onboarding-email' | 'addSource' | 'uploadAttachment' | 'imageViewer' | 'fileReader' | 'fileReaderTst' | 'attachmentViewer' | 'editCaseData' | 'addCollaborator' | 'importSummary' | 'declineEngagement' | 'signEngagement' | 'signFinalEngagement' | 'addTextContent' | 'editDraftInstruction' | 'editDescription' | 'editTone' | 'editWritingStyle' | 'giveAccess' | 'fileUploader' | 'confirmInvitation' | 'addMember' | 'linkCase' | 'editMatterData' | 'createClient' | 'editDepartment' | 'editTeam' | 'connectMatter' | 'uploadClientDocument' | 'addClientToMatter' | 'adminCreateRole' | 'adminEditRole' | 'adminAssignRole' | 'adminEditUser' | 'adminInviteUser' | 'versionHistory' | 'manageAccess' | 'templateCollaborators' | 'templatePreview' | 'uploadTableDocuments' | 'addTableColumn' | 'editTableColumn' | 'startReview' | 'reviewHelp' | 'playbookHelp' | 'replacePlaybookDocument' | 'tagPicker' | 'auditLogDetail' | 'editRateOverride' | 'editTimeEntry' | 'matterOutcome' | 'draftingAttachMatter' | 'researchShare' | 'playbookPicker' | 'templatePicker' | 'documentInfo';

/** Visual treatment for a modal - maps to a leading color band in GlobalModal. */
export type ModalVariant = 'info' | 'warning' | 'danger' | 'success';

/**
 * Canonical GlobalModal payload (#2332). NEW code should dispatch showModal with
 * this exact shape; it is structurally a subset of ModalState so it stays
 * assignable without breaking legacy callers. Legacy callers are migrated in
 * #2333; until then GlobalModal renders them via a back-compat shim.
 */
export interface GlobalModalPayload {
  type: ModalType;
  /** Headline shown next to the icon. */
  title: string;
  /** Primary body text. */
  message: string;
  /** Secondary, smaller muted text below the message. */
  subText?: string;
  /** Optional leading icon (react-icons/pi node). */
  icon?: React.ReactNode;
  /** Color treatment: info=blue, warning=amber, danger=red, success=green. */
  variant?: ModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  /** Explicit close handler (runs in addition to hiding the modal). */
  onClose?: () => void;
  data?: Record<string, unknown>;
}

export interface ModalState {
  showModal: boolean;
  type: ModalType;
  message: string;
  onConfirm?: (data: any, completeTask?: boolean) => void;
  onSave?: (data: any) => void;
  onCancel?: () => void;
  onRefresh?: () => void | Promise<void>;
  duration?: number;
  summaryData?: ImportSummary;
  folderOptions?: FolderProps[];
  selectedModel?: any;
  inputData?: string;
  info?: string;
  title?: string;
  date?: string;
  taskId?: any[];
  fileID?: string;
  width?: string;
  preSelectedFolderId?: string;
  subMsg?: string;
  // ── Canonical GlobalModal fields (#2332) ──
  subText?: string;
  variant?: ModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onClose?: () => void;
  data?: Record<string, unknown>;
  gap?: number;
  content?: React.ReactNode;
  component?: React.ReactNode;
  citations?: Citation[];
  citation?: Citation;
  tasks?: any[];
  clientId?: string;
  caseId?: string;
  allClients?: any[];
  selectedClients?: string[];
  sourceType?: string;
  files?: File[];
  setFiles?: (files: File[]) => void;
  imageUrl?: string;
  filename?: string;
  fileUrl?: string;
  fileType?: string;
  item?: any;
  category?: string;
  onFilesChange?: (files: File[]) => void;
  rows?: number;
  placeholder?: string;
  employees?: any;
  initialShared?: any;
  icon?: any;
  isDest?: boolean;
  existingMembers?: any[];
  teamId?: string;
  action?: 'AddMember' | 'LinkCase';
  selectedValue?: string | string[];
  matterId?: string;
  matterUuid?: string;
  taskUuid?: string;
  dataSourceUuid?: string;
  chatUuid?: string;
  roles?: DropdownOption[];
  department?: any;
  team?: any;
  rateOverride?: any;
  defaultCurrency?: string;
  timeEntry?: any;
  /** Matter outcome modal (#2240/#2241): 'close' captures + closes; 'edit' upserts. */
  outcomeMode?: 'close' | 'edit';
  outcome?: any;
  taxonomy?: any[];
  activityTypes?: any[];
  defaultUserId?: string;
  /** Pre-selected collaborator UUID for the time-entry / rate-override pickers (uuid-first writes). */
  defaultUserUuid?: string;
  onAfterSave?: () => void | Promise<void>;
  clientName?: string;
  clientUuid?: string;
  matters?: any[];
  onMatterConnected?: () => void;
  onDocumentUploadComplete?: () => void;
  totalMatterPages?: number;
  fetchMatters?: (query: string, page: number) => Promise<any[]>;
  role?: any;
  roleId?: string | number;
  user?: any;
  onRoleCreated?: () => void;
  onRoleUpdated?: () => void;
  onUserUpdated?: () => void;
  onInviteSent?: () => void;
  permissions?: any[];
  modules?: any[];
  clientRoleOptions?: DropdownOption[];
  fileId?: string;
  folderId?: string;
  fileName?: string;
  onVersionRestore?: () => void;
  orgType?: string;
  itemId?: string;
  itemType?: 'file' | 'folder';
  itemName?: string;
  itemCollaborators?: any[];
  creatorId?: string;
  // researchShare - sharing a research chat reuses CollaboratorSelector.
  // (`chatUuid` is already declared above and is reused here.)
  chatTitle?: string;
  collaborators?: any[];
  currentUserUuid?: string;
  onCollaboratorsChange?: (collaborators: any[]) => void;
  templateBranding?: any;
  tableUuid?: string;
  columnToEdit?: any;
  reviewUuid?: string;
  sessionUuid?: string;
  documentId?: number;
  documentName?: string;
  onFileSelected?: (file: File) => void;
  playbookUuid?: string;
  projectUuid?: string;
  maxFiles?: number;
  allowedExtensions?: string[];
  /** Per-file size cap for the modal's uploader. Defaults to FileUploader's own. */
  maxSizeBytes?: number;
  auditEntry?: any;
  tags?: any[];
  onTagsChanged?: (tags: any[]) => void;
  // playbookPicker - shared PlaybookPickerModal, opened by the create-table and
  // create-review forms. `onPlaybookSelect` round-trips the choice back into the
  // opening form's state (null = selection cleared).
  selectedPlaybookUuid?: string;
  onPlaybookSelect?: (playbook: PlaybookListItem | null) => void;
  // templatePicker - shared TemplatePickerModal, opened by the create-table
  // form. Same contract as playbookPicker: the opener passes the list it has
  // already loaded plus the current choice, and `onTemplateSelect` round-trips
  // the pick back into the form's state ('' = selection cleared).
  templates?: TableTemplate[];
  selectedTemplateId?: string;
  onTemplateSelect?: (templateId: string) => void;
  // draftingAttachMatter
  draftingKind?: string;
  draftingUuid?: string;
  existingMatterIds?: number[];
  // documentInfo - the file viewer's Info button (files-3). The document's
  // metadata on demand, so the viewport stays the document's.
  documentInfo?: DocumentInfo;
}

const initialState: ModalState = {
  showModal: false,
  type: 'custom',
  message: '',
  info: '',
  title: '',
  width: '',
  subMsg: '',
  inputData: '',
  folderOptions: undefined,
  selectedModel: undefined,
  date: '',
  taskId: [],
  fileID: '',
  preSelectedFolderId: '',
  duration: undefined,
  onConfirm: undefined,
  onCancel: undefined,
  clientId: '',
  caseId: '',
  files: [],
  onFilesChange: () => {},
};

export const modalReducer = (state = initialState, action: AnyAction): ModalState => {
  switch (action.type) {
    case 'SHOW_MODAL':
      return {
        ...state,
        ...action.payload,
        showModal: true,
        onFilesChange: action.payload.onFilesChange || state.onFilesChange,
      };
    case 'HIDE_MODAL':
      return {
        ...initialState,
        showModal: false,
      };
    default:
      return state;
  }
};
